import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { StepEsign, StepSanctionLetter } from "./ApplyLoanPage";
import "./ApplyLoanPage.css";
import "./SanctionLetterPage.css";

const API = (process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");

// What to show for each lead status that is NOT LEAD_APPROVED.
// step: how far along the 4-step strip the application is (0..3).
const BLOCKED_STATES = {
  NO_LEAD: {
    chip: "Not started",
    tone: "neutral",
    step: 0,
    title: "No application yet",
    message: "Start a loan application and your sanction letter will appear here once it is approved.",
    action: { to: "/dashboard/apply-loan", label: "Apply for a loan" },
  },
  IN_PROGRESS: {
    chip: "In progress",
    tone: "info",
    step: 0,
    title: "Finish your application first",
    message: "Your application is still in progress. Complete the remaining steps and submit it for review.",
    action: { to: "/dashboard/apply-loan", label: "Continue application" },
  },
  WITH_AGENT: {
    chip: "Under review",
    tone: "info",
    step: 1,
    title: "Your application is being reviewed",
    message: "We are checking your details. Your sanction letter will be ready here as soon as it is approved.",
    action: { to: "/dashboard", label: "Back to dashboard" },
  },
  DOCS_AWAITED: {
    chip: "Documents needed",
    tone: "warn",
    step: 1,
    title: "We need a few more documents",
    message: "Upload the documents requested on your application so the review can continue.",
    action: { to: "/dashboard/documents", label: "Upload documents" },
  },
  AGENT_REJECTED: {
    chip: "Not approved",
    tone: "danger",
    step: 2,
    title: "This application was not approved",
    message: "A sanction letter is only issued for approved applications. Our team can help you understand the decision.",
    action: { to: "/dashboard/support", label: "Contact support" },
  },
  LEAD_REJECTED: {
    chip: "Not approved",
    tone: "danger",
    step: 2,
    title: "This application was not approved",
    message: "A sanction letter is only issued for approved applications. Our team can help you understand the decision.",
    action: { to: "/dashboard/support", label: "Contact support" },
  },
  UNKNOWN: {
    chip: "Status unavailable",
    tone: "neutral",
    step: 0,
    title: "We couldn't check your application",
    message: "Please refresh the page or try again in a moment.",
    action: { to: "/dashboard", label: "Back to dashboard" },
  },
};

const STEPS = ["Applied", "Under review", "Approved", "Sanction letter"];

function BlockedState({ status, error }) {
  const state = BLOCKED_STATES[status] || BLOCKED_STATES.UNKNOWN;
  const rejected = state.tone === "danger";
  return (
    <section className="sl-workspace sl-blocked" aria-label="Sanction letter unavailable">
      <div className={`sl-blocked-hero is-${state.tone}`}>
        <div className="sl-blocked-icon" aria-hidden="true">
          {rejected ? "✕" : status === "DOCS_AWAITED" ? "!" : "⏳"}
        </div>
        <span className={`sl-chip is-${state.tone}`}>{state.chip}</span>
        <h2>{state.title}</h2>
        <p role="alert">{error || state.message}</p>
      </div>

      <ol className="sl-steps" aria-label="Application progress">
        {STEPS.map((label, index) => {
          const cls =
            index < state.step
              ? "is-done"
              : index === state.step
                ? rejected
                  ? "is-stopped"
                  : "is-current"
                : "";
          return (
            <li key={label} className={cls}>
              <span className="sl-steps-dot" aria-hidden="true">
                {index < state.step ? "✓" : index === state.step && rejected ? "✕" : index + 1}
              </span>
              <span className="sl-steps-label">{label}</span>
            </li>
          );
        })}
      </ol>

      <div className="sl-blocked-actions">
        <Link className="sl-btn sl-btn-primary" to={state.action.to}>
          {state.action.label}
        </Link>
        {state.action.to !== "/dashboard" && (
          <Link className="sl-btn sl-btn-ghost" to="/dashboard">
            Back to dashboard
          </Link>
        )}
      </div>
    </section>
  );
}

export default function SanctionLetterPage() {
  const [activePanel, setActivePanel] = useState("letter");
  const [leadStatus, setLeadStatus] = useState(null); // null = checking
  const [checkError, setCheckError] = useState("");
  const session = JSON.parse(localStorage.getItem("userSession") || "{}");
  const token = session?.token || "";

  // The page is only usable once the lead is LEAD_APPROVED (the backend
  // enforces the same rule on every sanction-letter / eSign endpoint).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API}/api/lead/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        });
        const json = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok || !json.success) throw new Error();
        setLeadStatus(String(json.leadStatus || "NO_LEAD").toUpperCase());
      } catch {
        if (!cancelled) {
          setLeadStatus("UNKNOWN");
          setCheckError("We couldn't check your application status. Please try again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (leadStatus === null) {
    return (
      <div className="sl-root">
        <section className="sl-workspace sl-blocked sl-checking" aria-label="Sanction letter">
          <div className="sl-spinner" aria-hidden="true" />
          <p role="status">Checking your application status…</p>
        </section>
      </div>
    );
  }

  if (leadStatus !== "LEAD_APPROVED") {
    return (
      <div className="sl-root">
        <BlockedState status={leadStatus} error={checkError} />
      </div>
    );
  }

  return (
    <div className="sl-root">
      <section className="sl-workspace" aria-label="Sanction letter and eSign">
        <div className="sl-tabs" role="tablist" aria-label="Sanction letter steps">
          <button
            type="button"
            role="tab"
            aria-selected={activePanel === "letter"}
            className={`sl-tab ${activePanel === "letter" ? "is-active" : ""}`}
            onClick={() => setActivePanel("letter")}
          >
            <span aria-hidden="true">1</span>
            Review letter
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePanel === "esign"}
            className={`sl-tab ${activePanel === "esign" ? "is-active" : ""}`}
            onClick={() => setActivePanel("esign")}
          >
            <span aria-hidden="true">2</span>
            Aadhaar eSign
          </button>
        </div>

        <div className="sl-panel" role="tabpanel">
          {activePanel === "letter" ? (
            <StepSanctionLetter token={token} onProceed={() => setActivePanel("esign")} />
          ) : (
            <StepEsign token={token} onBack={() => setActivePanel("letter")} />
          )}
        </div>
      </section>
    </div>
  );
}
